import { useMemo } from "react";
import { Button, Modal, Progress } from "semantic-ui-react";

import { Option } from "models/Option";
import { SelectedCarrier } from "models/SelectedCarrier";
import CarrierCapacityShipmentMatchTable from "./CarrierCapacityShipmentMatchTable";
import { useGetCarrierCapacityShipmentMatches } from "../hooks/useGetCarrierCapacityShipmentMatches";
import { useGetCarrierCapacities } from "../hooks/useGetCarrierCapacities";
import { CarrierCapacityInfoDto } from "models/dto/CarrierCapacityInfoDto";

import styles from "./CarrierCapacityShipmentMatchModal.module.scss";

type CarrierCapacityShipmentMatchModalProps = {
  carrier: SelectedCarrier;
  equipmentOptions: Option<number>[];
  capacities: ReturnType<
    typeof useGetCarrierCapacities<"Capacity" | "NoCapacity">
  >;
  preferredLanes: ReturnType<typeof useGetCarrierCapacities<"Preferred Lane">>;
  onClose: () => void;
};

const CarrierCapacityShipmentMatchModal = (
  props: CarrierCapacityShipmentMatchModalProps
) => {
  const { capacities, preferredLanes } = props;

  const capacityData = useMemo(() => {
    const filterOnlyCapacity = (
      capacity: CarrierCapacityInfoDto<"Capacity" | "NoCapacity">
    ): capacity is CarrierCapacityInfoDto<"Capacity"> =>
      capacity.type === "Capacity";

    return capacities.data?.filter(filterOnlyCapacity) ?? [];
  }, [capacities.data]);

  const preferredLaneData = preferredLanes.data ?? [];

  const capacityShipmentMatches = useGetCarrierCapacityShipmentMatches({
    carrierId: props.carrier.id,
    capacities: capacityData,
  });

  const preferredLaneShipmentMatches = useGetCarrierCapacityShipmentMatches({
    carrierId: props.carrier.id,
    capacities: preferredLaneData,
  });

  return (
    <Modal size="fullscreen" open onClose={props.onClose}>
      <Modal.Header>
        <div className={styles.header}>
          <div>Suggested Shipments - {props.carrier.name}</div>
          <div className={styles.headerMatchGrid}>
            {capacityData.length > 0 ? (
              <>
                <div className={styles.headerMatchGridProgress}>
                  {capacityShipmentMatches.sortedCapacityIds.pending.length >
                  0 ? (
                    <Progress
                      active
                      size="small"
                      color="blue"
                      value={
                        capacityData.length -
                        capacityShipmentMatches.sortedCapacityIds.pending.length
                      }
                      total={capacityData.length}
                    />
                  ) : null}
                </div>
                <div>Capacity Shipment(s) Found:</div>
                <div className={styles.headerMatchGridQuantity}>
                  {capacityShipmentMatches.totalCapacityShipments}
                </div>
              </>
            ) : null}

            {preferredLaneData.length > 0 ? (
              <>
                <div className={styles.headerMatchGridProgress}>
                  {preferredLaneShipmentMatches.sortedCapacityIds.pending
                    .length > 0 ? (
                    <Progress
                      active
                      size="small"
                      color="blue"
                      value={
                        preferredLaneData.length -
                        preferredLaneShipmentMatches.sortedCapacityIds.pending
                          .length
                      }
                      total={preferredLaneData.length}
                    />
                  ) : null}
                </div>
                <div>Preferred Lane Shipment(s) Found:</div>
                <div className={styles.headerMatchGridQuantity}>
                  {preferredLaneShipmentMatches.totalCapacityShipments}
                </div>
              </>
            ) : null}
          </div>
        </div>
      </Modal.Header>

      <Modal.Content scrolling>
        {capacityData.length > 0 ? (
          <CarrierCapacityShipmentMatchTable
            {...capacityShipmentMatches}
            type="Capacity"
            equipmentOptions={props.equipmentOptions}
            isLoadingCapacities={capacities.isLoading}
            capacities={capacityData}
          />
        ) : null}

        {preferredLaneData.length > 0 ? (
          <CarrierCapacityShipmentMatchTable
            {...preferredLaneShipmentMatches}
            type="Preferred Lane"
            equipmentOptions={props.equipmentOptions}
            isLoadingCapacities={preferredLanes.isLoading}
            capacities={preferredLaneData}
          />
        ) : null}
      </Modal.Content>

      <Modal.Actions>
        <Button type="button" negative onClick={props.onClose}>
          Close
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default CarrierCapacityShipmentMatchModal;
